
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Share2, Upload, TwitchIcon, Instagram, Twitter, Youtube, Smartphone, ArrowRightLeft, CheckCircle2, Copy, RefreshCw } from 'lucide-react';

interface PlatformFormat {
  id: string;
  name: string;
  icon: React.ReactNode;
  dimensions: string;
  duration: string;
  formats: string[];
}

const PLATFORM_FORMATS: PlatformFormat[] = [
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: <TwitchIcon className="h-5 w-5" />,
    dimensions: '9:16 (1080×1920)',
    duration: '15-60 seconds',
    formats: ['MP4', 'MOV']
  },
  {
    id: 'instagram-reel',
    name: 'Instagram Reel',
    icon: <Instagram className="h-5 w-5" />,
    dimensions: '9:16 (1080×1920)',
    duration: 'Up to 90 seconds',
    formats: ['MP4', 'MOV']
  },
  {
    id: 'instagram-post',
    name: 'Instagram Post',
    icon: <Instagram className="h-5 w-5" />,
    dimensions: '1:1 (1080×1080) or 4:5 (1080×1350)',
    duration: 'N/A',
    formats: ['JPG', 'PNG']
  },
  {
    id: 'youtube-short',
    name: 'YouTube Short',
    icon: <Youtube className="h-5 w-5" />,
    dimensions: '9:16 (1080×1920)',
    duration: 'Up to 60 seconds',
    formats: ['MP4']
  },
  {
    id: 'twitter-video',
    name: 'Twitter Video',
    icon: <Twitter className="h-5 w-5" />,
    dimensions: '16:9 (1280×720)',
    duration: 'Up to 2 minutes 20 seconds',
    formats: ['MP4', 'MOV']
  },
];

const ContentReshaper: React.FC = () => {
  const { toast } = useToast();
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasProcessed, setHasProcessed] = useState(false);
  
  const toggleTarget = (targetId: string) => {
    if (selectedTargets.includes(targetId)) {
      setSelectedTargets(prev => prev.filter(id => id !== targetId));
    } else {
      setSelectedTargets(prev => [...prev, targetId]);
    }
  };
  
  const handleProcessContent = () => {
    if (!selectedSource || selectedTargets.length === 0) {
      toast({
        title: "Selection required",
        description: "Please select both source and target platforms",
        variant: "destructive"
      });
      return;
    }
    
    setIsProcessing(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsProcessing(false);
      setHasProcessed(true);
      
      toast({
        title: "Content reshaped!",
        description: `Your content has been optimized for ${selectedTargets.length} platforms.`
      });
    }, 2000);
  };
  
  const handleDownload = () => {
    toast({
      title: "Content ready for download",
      description: "All selected formats have been prepared and are ready to download."
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold mb-1">Content Reshaper</h2>
          <p className="text-muted-foreground">Transform your content for multiple platforms with AI</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-background/70">
            <CardHeader className="pb-2">
              <CardTitle>Content Transformation</CardTitle>
              <CardDescription>Upload once, reshape for multiple platforms</CardDescription>
            </CardHeader>
            <CardContent>
              {!hasProcessed ? (
                <div className="space-y-6">
                  <div className="border border-dashed rounded-lg p-8">
                    <div className="flex flex-col items-center text-center">
                      <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium mb-2">Upload your content</h3>
                      <p className="text-muted-foreground mb-4 max-w-md">
                        Upload a video, image, or audio file and our AI will reshape it for your selected platforms
                      </p>
                      <div className="flex gap-3">
                        <Button>Upload File</Button>
                        <Button variant="outline">YouTube Link</Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-base font-medium mb-3">1. Choose Source Format</h3>
                      <Select value={selectedSource || undefined} onValueChange={setSelectedSource}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select source format" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="youtube-long">YouTube Long Video</SelectItem>
                          <SelectItem value="podcast">Podcast/Audio</SelectItem>
                          <SelectItem value="instagram-post">Instagram Post</SelectItem>
                          <SelectItem value="blog-post">Blog Post/Article</SelectItem>
                          <SelectItem value="raw-footage">Raw Footage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <h3 className="text-base font-medium mb-3">2. Select Target Platforms</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {PLATFORM_FORMATS.map((platform) => (
                          <div
                            key={platform.id}
                            className={`p-2 border rounded-md flex flex-col items-center text-center cursor-pointer transition-colors ${
                              selectedTargets.includes(platform.id)
                                ? 'bg-purple-500/10 border-purple-500/50'
                                : 'bg-muted/20 border-transparent hover:bg-muted/30'
                            }`}
                            onClick={() => toggleTarget(platform.id)}
                          >
                            <div className="mb-1">
                              {platform.icon}
                            </div>
                            <span className="text-sm font-medium">{platform.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-center">
                    <Button 
                      size="lg"
                      onClick={handleProcessContent}
                      disabled={isProcessing || !selectedSource || selectedTargets.length === 0}
                    >
                      {isProcessing ? (
                        <span className="flex items-center">
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <ArrowRightLeft className="h-4 w-4 mr-2" />
                          Reshape Content
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center gap-3">
                    <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-green-500">Content successfully reshaped!</h3>
                      <p className="text-sm text-muted-foreground">
                        Your content has been optimized for {selectedTargets.length} different platforms
                      </p>
                    </div>
                  </div>
                  
                  <Tabs defaultValue="tiktok" className="w-full">
                    <TabsList className="grid grid-cols-3 mb-4">
                      <TabsTrigger value="tiktok" className="data-[state=active]:bg-background rounded-none py-2">
                        <div className="flex items-center gap-2">
                          <TwitchIcon className="h-4 w-4" />
                          <span>TikTok</span>
                        </div>
                      </TabsTrigger>
                      <TabsTrigger value="instagram" className="data-[state=active]:bg-background rounded-none py-2">
                        <div className="flex items-center gap-2">
                          <Instagram className="h-4 w-4" />
                          <span>Instagram</span>
                        </div>
                      </TabsTrigger>
                      <TabsTrigger value="youtube" className="data-[state=active]:bg-background rounded-none py-2">
                        <div className="flex items-center gap-2">
                          <Youtube className="h-4 w-4" />
                          <span>YouTube</span>
                        </div>
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="tiktok" className="space-y-4">
                      <div className="aspect-[9/16] bg-black/70 rounded-lg flex items-center justify-center">
                        <div className="text-center p-4">
                          <Smartphone className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">TikTok video preview</p>
                        </div>
                      </div>
                      
                      <div className="flex justify-between">
                        <div>
                          <p className="text-sm font-medium">TikTok Format</p>
                          <p className="text-xs text-muted-foreground">9:16 ratio, 30 seconds</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Copy className="h-4 w-4 mr-1" />
                            Copy Caption
                          </Button>
                          <Button size="sm">
                            Download
                          </Button>
                        </div>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="instagram" className="space-y-4">
                      <div className="aspect-[9/16] bg-black/70 rounded-lg flex items-center justify-center">
                        <div className="text-center p-4">
                          <Smartphone className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">Instagram Reel preview</p>
                        </div>
                      </div>
                      
                      <div className="flex justify-between">
                        <div>
                          <p className="text-sm font-medium">Instagram Reel Format</p>
                          <p className="text-xs text-muted-foreground">9:16 ratio, 60 seconds</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Copy className="h-4 w-4 mr-1" />
                            Copy Caption
                          </Button>
                          <Button size="sm">
                            Download
                          </Button>
                        </div>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="youtube" className="space-y-4">
                      <div className="aspect-video bg-black/70 rounded-lg flex items-center justify-center">
                        <div className="text-center p-4">
                          <Youtube className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">YouTube Short preview</p>
                        </div>
                      </div>
                      
                      <div className="flex justify-between">
                        <div>
                          <p className="text-sm font-medium">YouTube Short Format</p>
                          <p className="text-xs text-muted-foreground">9:16 ratio, 60 seconds</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline">
                            <Copy className="h-4 w-4 mr-1" />
                            Copy Description
                          </Button>
                          <Button size="sm">
                            Download
                          </Button>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                  
                  <div className="flex justify-center">
                    <Button variant="outline" onClick={handleDownload}>
                      Download All Formats
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Platform Specifications</CardTitle>
              <CardDescription>Optimal formats for each platform</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-4">
                  {PLATFORM_FORMATS.map((platform) => (
                    <div key={platform.id} className="p-3 bg-muted/30 rounded-md">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="p-1.5 bg-muted/50 rounded">
                          {platform.icon}
                        </div>
                        <h3 className="font-medium">{platform.name}</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm">
                        <div className="text-muted-foreground">Dimensions:</div>
                        <div>{platform.dimensions}</div>
                        <div className="text-muted-foreground">Duration:</div>
                        <div>{platform.duration}</div>
                        <div className="text-muted-foreground">Formats:</div>
                        <div>{platform.formats.join(', ')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Recently Reshaped</CardTitle>
              <CardDescription>Your recent content transformations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-muted/30 rounded-md">
                <div className="flex justify-between items-start mb-1">
                  <h3 className="font-medium">Summer Tour Announcement</h3>
                  <Badge variant="outline" className="text-xs">2 days ago</Badge>
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <Badge variant="outline" className="bg-background/20 p-1.5">
                    <Youtube className="h-3.5 w-3.5" />
                  </Badge>
                  <span className="text-muted-foreground">→</span>
                  <Badge variant="outline" className="bg-background/20 p-1.5">
                    <TwitchIcon className="h-3.5 w-3.5" />
                  </Badge>
                  <Badge variant="outline" className="bg-background/20 p-1.5">
                    <Instagram className="h-3.5 w-3.5" />
                  </Badge>
                  <Badge variant="outline" className="bg-background/20 p-1.5">
                    <Twitter className="h-3.5 w-3.5" />
                  </Badge>
                </div>
              </div>
              
              <div className="p-3 bg-muted/30 rounded-md">
                <div className="flex justify-between items-start mb-1">
                  <h3 className="font-medium">Studio Session Highlights</h3>
                  <Badge variant="outline" className="text-xs">5 days ago</Badge>
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <Badge variant="outline" className="bg-background/20 p-1.5">
                    <Youtube className="h-3.5 w-3.5" />
                  </Badge>
                  <span className="text-muted-foreground">→</span>
                  <Badge variant="outline" className="bg-background/20 p-1.5">
                    <TwitchIcon className="h-3.5 w-3.5" />
                  </Badge>
                  <Badge variant="outline" className="bg-background/20 p-1.5">
                    <Instagram className="h-3.5 w-3.5" />
                  </Badge>
                </div>
              </div>
              
              <Button variant="outline" className="w-full mt-1">
                <Share2 className="h-4 w-4 mr-1" />
                View All Reshapes
              </Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Cross-Posting Tips</CardTitle>
              <CardDescription>How to effectively reuse content</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p>• Adapt your message for each platform's unique audience</p>
                <p>• Use platform-specific features (e.g., Instagram stickers)</p>
                <p>• Stagger posts across platforms for maximum reach</p>
                <p>• Customize captions for each platform's style</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContentReshaper;
